/** CMS-485 form mappers between API payloads and react-hook-form state. */

import type {
  Cms485AllergyRow,
  Cms485DiagnosisRow,
  Cms485FormResponse,
  Cms485OrderDataPayload,
  OrderResponse,
} from '../services/api/types.gen';
import type { Cms485FormValues } from '../types/cms485.types';

/** User-editable string fields persisted in order_data (print-only blocks omitted). */
const CMS485_EDITABLE_STRING_FIELD_KEYS = [
  'soc_date',
  'primary_language',
  'risk_profile',
  'primary_diagnosis_code',
  'primary_diagnosis_description',
  'primary_diagnosis_date',
  'mental_status',
  'dme_supplies',
  'prognosis',
  'safety_measures',
  'nutritional_requirements',
  'medications',
  'orders_and_treatments',
  'goals',
  'homebound_certification_statement',
] as const;

type Cms485EditableStringFieldKey =
  (typeof CMS485_EDITABLE_STRING_FIELD_KEYS)[number];

export function listToTextareaValue(items: string[] | undefined): string {
  return (items ?? []).join('\n');
}

export function textareaValueToList(value: string | undefined): string[] {
  if (!value?.trim()) return [];
  return value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
}

function stringFieldsFromRecord(
  data: Record<string, unknown>
): Record<Cms485EditableStringFieldKey, string> {
  return Object.fromEntries(
    CMS485_EDITABLE_STRING_FIELD_KEYS.map(key => [
      key,
      (data[key] as string) ?? '',
    ])
  ) as Record<Cms485EditableStringFieldKey, string>;
}

function stringFieldsFromForm(
  form: Cms485FormResponse
): Record<Cms485EditableStringFieldKey, string> {
  return Object.fromEntries(
    CMS485_EDITABLE_STRING_FIELD_KEYS.map(key => [key, form[key] ?? ''])
  ) as Record<Cms485EditableStringFieldKey, string>;
}

function normalizeDiagnosisRows(
  rows: Cms485DiagnosisRow[] | undefined
): Array<Required<Cms485DiagnosisRow>> {
  return (rows ?? []).map(row => ({
    code: row.code ?? '',
    description: row.description ?? '',
    date: row.date ?? '',
  }));
}

function normalizeAllergyRows(
  rows: Cms485AllergyRow[] | undefined
): Array<Required<Cms485AllergyRow>> {
  return (rows ?? []).map(row => ({
    substance: row.substance ?? '',
    reaction: row.reaction ?? '',
  }));
}

export const EMPTY_FORM_VALUES: Cms485FormValues = {
  linked_oasis_visit_id: '',
  secondary_diagnoses: [],
  allergies: [],
  functional_limitations: '',
  activities_permitted: '',
  ...(Object.fromEntries(
    CMS485_EDITABLE_STRING_FIELD_KEYS.map(key => [key, ''])
  ) as Record<Cms485EditableStringFieldKey, string>),
};

export function formResponseToFormValues(
  form: Cms485FormResponse,
  visit: OrderResponse
): Cms485FormValues {
  return {
    ...EMPTY_FORM_VALUES,
    ...stringFieldsFromForm(form),
    linked_oasis_visit_id: visit.linked_oasis_visit_id ?? '',
    secondary_diagnoses: normalizeDiagnosisRows(form.secondary_diagnoses),
    allergies: normalizeAllergyRows(form.allergies),
    functional_limitations: listToTextareaValue(form.functional_limitations),
    activities_permitted: listToTextareaValue(form.activities_permitted),
  };
}

export function visitToFormValues(visit: OrderResponse): Cms485FormValues {
  const data = (visit.order_data ?? {}) as Record<string, unknown>;
  return {
    ...EMPTY_FORM_VALUES,
    linked_oasis_visit_id: visit.linked_oasis_visit_id ?? '',
    ...stringFieldsFromRecord(data),
    secondary_diagnoses: normalizeDiagnosisRows(
      data.secondary_diagnoses as Cms485DiagnosisRow[] | undefined
    ),
    allergies: normalizeAllergyRows(
      data.allergies as Cms485AllergyRow[] | undefined
    ),
    functional_limitations: listToTextareaValue(
      data.functional_limitations as string[] | undefined
    ),
    activities_permitted: listToTextareaValue(
      data.activities_permitted as string[] | undefined
    ),
  };
}

export function formValuesToOrderData(
  formValues: Cms485FormValues
): { payload_kind: 'cms485' } & Cms485OrderDataPayload {
  const strings = Object.fromEntries(
    CMS485_EDITABLE_STRING_FIELD_KEYS.map(key => [key, formValues[key] ?? ''])
  );

  return {
    payload_kind: 'cms485',
    linked_oasis_visit_id: formValues.linked_oasis_visit_id,
    ...strings,
    secondary_diagnoses: formValues.secondary_diagnoses,
    functional_limitations: textareaValueToList(
      formValues.functional_limitations
    ),
    activities_permitted: textareaValueToList(formValues.activities_permitted),
    allergies: formValues.allergies,
  };
}

/**
 * Build the PATCH body for a CMS-485 / Post Hospital Order autosave.
 *
 * `linked_oasis_visit_id` is read back from the column of that name, not from
 * `order_data`, so an order form that sends it only inside the JSONB payload
 * shows the picker going empty the next time the order is opened (EMR-1312).
 * The field lives here rather than in each form's save handler so a new order
 * form with a visit picker cannot leave it out.
 *
 * An empty-string selection normalizes to `null`: the backend treats an
 * explicit null as "clear the link", and "" is not a UUID.
 */
export function buildOrderSaveBody<TOrderData, TStamp>({
  orderData,
  linkedOasisVisitId,
  orderDataLastUpdated,
}: {
  orderData: TOrderData;
  linkedOasisVisitId: string | null | undefined;
  orderDataLastUpdated: TStamp;
}): {
  order_data: TOrderData;
  order_data_last_updated: TStamp;
  linked_oasis_visit_id: string | null;
} {
  return {
    order_data: orderData,
    order_data_last_updated: orderDataLastUpdated,
    linked_oasis_visit_id: linkedOasisVisitId || null,
  };
}
